<template>
  <div class="container-fluid py-4">
    <!-- 상단 카드 컴포넌트 영역 -->
    <div class="row mb-4">
      <div class="col-12">
        <OrdersInfo :card-info="cardInfo"/>
      </div>
    </div>

    <!-- 검색 필터 영역 -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="order-search-filter-row">
          <div class="search-filter-label">검색어</div>
          <div class="search-filter-content">
            <a-select v-model:value="searchFilter.filter" class="filter-select">
              <a-select-option value="hospitalName">병원명</a-select-option>
              <a-select-option value="hospitalId">병원 아이디</a-select-option>
            </a-select>
            <a-input
              v-model:value="searchFilter.keyword"
              :placeholder="getSearchPlaceholder"
              class="search-input"
              @pressEnter="handleSearch"
            />
            <div class="button-group">
              <a-button type="primary" class="search-button" @click="handleSearch">검색</a-button>
              <a-button class="reset-button" @click="resetSearch">초기화</a-button>
            </div>
          </div>
        </div>

        <div class="order-search-filter-row">
          <div class="search-filter-label">조회 기간</div>
          <div class="search-filter-content">
            <div class="date-filter-buttons">
              <a-button 
                :class="{ active: selectedPeriod === 'all' }" 
                @click="setPeriod('all')"
              >
                전체
              </a-button>
              <a-button 
                :class="{ active: selectedPeriod === '3months' }" 
                @click="setPeriod('3months')"
              >
                최근 3개월
              </a-button>
              <a-button 
                :class="{ active: selectedPeriod === '1year' }" 
                @click="setPeriod('1year')"
              >
                최근 1년
              </a-button>
              <a-button 
                :class="{ active: selectedPeriod === '3years' }" 
                @click="setPeriod('3years')"
              >
                최근 3년
              </a-button>
              <a-button 
                :class="{ active: selectedPeriod === 'custom' }" 
                @click="setPeriod('custom')"
              >
                기간 설정
              </a-button>
            </div>
            <a-range-picker 
              v-model:value="dateRange"
              :disabled="selectedPeriod !== 'custom'"
              format="YYYY-MM-DD"
              @change="onDateRangeChange"
              @pressEnter="handleCustomDateSearch"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- 테이블 영역 -->
    <div class="card">
      <div class="card-header">
        <div class="d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center gap-3">
            <div class="table-info">
              전체 <span class="text-primary">{{ totalCount }}</span>건
            </div>        
          </div>
          
          <div class="d-flex align-items-center gap-3">
            <a-button 
              type="primary" 
              class="search-button" 
              @click="goToHospitalManagement"
            >
              병원목록
            </a-button>
            <a-select 
              v-model:value="pageSize" 
              class="page-size-select"
              :options="pageSizes"
              @change="onPageSizeChange"
            />
          </div>
        </div>
      </div>

      <div class="card-body table-container">
        <a-tabs v-model:activeKey="activeTab" @change="handleTabChange">
          <a-tab-pane key="cashFlow" tab="충전금 이력">
            <CashFlow 
              :loading-states="loadingStates"
              :cash-flow="cashFlow"
              :paging="paging"
              :search-delivery="onSearchDelivery"
            />
          </a-tab-pane>
          <a-tab-pane key="delivery" tab="배송관리">
            <DeliveryManagement 
              :loading-states="loadingStates"
              :invoice="invoice"
              :paging="paging"
              :search-delivery="onSearchDelivery"
            />
          </a-tab-pane>
        </a-tabs>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import dayjs from "dayjs";
import locale from 'ant-design-vue/es/date-picker/locale/ko_KR';
import PageHeader from '@/views/components/PageHeader.vue';
import CashFlow from "./components/CashFlow.vue";
import OrdersInfo from "./components/OrdersInfo.vue";
import DeliveryManagement from "./components/DeliveryManagement.vue";
import {
  compareUnixDates,
  convertMonthAndYearText,
  formatDate,
  logging,
  oneBtnDialog
} from "@/utils/utils";
import { serverEnum } from "./server-enum";

// API 관련 로직을 별도 객체로 분리
const orderApi = {
  async fetchOrders(apiInstance, params) {
    const response = await apiInstance.post('/orders', params);
    if (response.status !== 200 && response.status !== 201) return null;
    return response.data;
  },

  async searchDelivery(apiInstance, param) {
    const response = await apiInstance.get(`/orders/delivery/ilyang/${param}`);
    if (response.status !== 200 && response.status !== 201) return null;
    return response.data;
  }
};

export default defineComponent({
  name: 'OrderContainer',

  components: {
    PageHeader,
    CashFlow,
    OrdersInfo,
    DeliveryManagement
  },

  setup() {
    // API 관련 메서드들을 컴포넌트에서 사용하기 쉽게 래핑
    const apiHandler = {
      async getOrders(instance, params) {
        try {
          return await orderApi.fetchOrders(instance.$apiInstance, params);
        } catch (error) {
          console.error('Order fetch failed:', error);
          return null;
        }
      },

      async searchDelivery(instance, param) {
        try {
          return await orderApi.searchDelivery(instance.$apiInstance, param);
        } catch (error) {
          console.error('Delivery search failed:', error);
          return null;
        }
      }
    };

    return {
      apiHandler
    };
  },

  data() {
    return {
      locale,
      dateRanges: [],
      tabs: {
        CashFlow: 'cash-flow',
        DeliveryManagement: 'delivery-management'
      },
      currentTab: 'cash-flow',
      loadingStates: {
        delivering: false,
        completed: false
      },
      unpaidAmtNoti: false,

      // 검 필터 관련
      searchFilter: {
        filter: 'hospitalName',
        keyword: '',
        startAt: null,
        endAt: null,
        selectedDateFilter: 'all',
        dateFilters: [3, 12, 36, 'etc'],
      },

      // 카드 정보
      cardInfo: {
        deptName: '',
        empName: '',
        empMobile: '',
        totalDeliveryCount: 0,
        totalCompletedCount: 0,
        totalRemainAmt: '',
      },

      // 페이징 정보
      paging: {
        limit: 10,
        currentPage: 1,
        totalPages: 0
      },

      // 데이터
      cashFlow: {
        completed: { list: [], totalCount: 0 },
        delivering: { list: [], totalCount: 0 }
      },
      invoice: {
        completed: { list: [], totalCount: 0, totalPages: 1, selectedPageNum: 1 },
        delivering: { list: [], totalCount: 0, totalPages: 1, selectedPageNum: 1 }
      },
      pageSize: 10,
      selectedPeriod: 'all',
      dateRange: []
    };
  },

  computed: {
    currentTabInfo() {
      const isCashFlow = this.currentTab === this.tabs.CashFlow;
      const data = isCashFlow ? this.cashFlow : this.invoice;

      return {
        totalCount: data.completed.totalCount + data.delivering.totalCount,
        searchDateTxt: '주문일'
      };
    },

    pageSizes() {
      return [10, 50, 100, 150, 200, 250, 300].map(size => ({
        value: size,
        label: `${size}개씩 보기`
      }));
    },

    dateFilterText() {
      return (item) => {
        return item === 'etc' ? '기간 설정' : `최근 ${convertMonthAndYearText(item)}`;
      };
    },

    totalCount() {
      const { completed, delivering } = this.currentTab === this.tabs.CashFlow 
        ? this.cashFlow 
        : this.invoice;
      return (completed?.totalCount || 0) + (delivering?.totalCount || 0);
    },

    // 검색창 placeholder 동적 변경
    getSearchPlaceholder() {
      return this.searchFilter.filter === 'hospitalName' 
        ? '병원명을 정확하게 입력해주세요'
        : '병원 아이디를 입력해주세요';
    }
  },

  created() {
    // 페이지네이션 리스너 초기화
    this.paging.changePageListener = this.onPageChange;
    
    // 쿼리 파라미터 확인 및 검색 조건 적용
    this.initializeFromQueryParams();
  },

  async mounted() {
    try {
      await this.handleSearch();
    } catch (error) {
      console.error('Initial search failed:', error);
    }
  },

  methods: {
    // 쿼리 파라미터 초기화 메서드 추가
    initializeFromQueryParams() {
      const query = this.$route.query;
      
      // hospitalName 쿼리 파라미터가 존재하는 경우
      if (query.hospitalName) {
        this.searchFilter.filter = 'hospitalName';
        this.searchFilter.keyword = query.hospitalName;
        this.handleSearch();
      }
    },

    // 검색 조건이 변경될 때 URL 업데이트
    updateQueryParams() {
      const query = {};
      
      if (this.searchFilter.filter === 'hospitalName' && this.searchFilter.keyword) {
        query.hospitalName = this.searchFilter.keyword;
      }

      // 현재 라우트의 다른 쿼리 파라미터 유지
      const currentQuery = { ...this.$route.query };
      
      // 라우터 업데이트
      this.$router.replace({ 
        query: {
          ...currentQuery,
          ...query
        }
      }).catch(() => {});
    },

    // UI 핸들링 관련 메서드들
    async handleDateFilter(value) {
      this.searchFilter.selectedDateFilter = value;
      this.dateRanges = [];

      if (value === 'all') {
        await this.handleSearch({
          startAt: null,
          endAt: null
        });
        return;
      }

      if (value === 'etc') {
        const now = dayjs();
        this.dateRanges = [now, now];
        await this.handleSearch({
          startAt: formatDate().getCurrentUnixTime(),
          endAt: formatDate().getCurrentUnixTime()
        });
        return;
      }

      await this.handleSearch({
        startAt: formatDate().getPastDatesInUnixTime(value),
        endAt: formatDate().getCurrentUnixTime()
      });
    },

    async handleTabChange(value) {
      this.currentTab = value;
      await this.handleSearch();
    },

    onOrderUpdate({ updateOrder }) {
      if (updateOrder) {
        this.handleSearch();
      }
    },

    async onSearchDelivery(success, fail, param) {
      try {
        const response = await this.$apiInstance.get(`/orders/delivery/ilyang/${param}`);
        if (response.status === 200 || response.status === 201) {
          if (Array.isArray(response.data) && response.data[0]) {
            const deliveryData = response.data[0];
            // 모달 표시를 위한 데이터 구조
            const formattedData = {
              success: true,  // 성공 여부
              data: {
                invoiceNo: deliveryData.blNum,                    // 운송장 번호
                receiverName: deliveryData.rcvCoName,             // 수취인명 (rcvName이 아닌 rcvCoName 사용)
                receiverAddr: `${deliveryData.rcvAddr1} ${deliveryData.rcvAddr2}`.trim(), // 배송지
                receiverTel: deliveryData.rcvTel1,               // 수취인 전화번호
                senderName: deliveryData.sndCoName,              // 발신인명 (sndName이 아닌 sndCoName 사용)
                senderAddr: `${deliveryData.sndAddr1} ${deliveryData.sndAddr2}`.trim(),   // 발신지
                senderTel: deliveryData.sndTel1,                 // 발신인 전화번호
                itemName: deliveryData.prodContent,              // 상품명
                deliveryStatus: deliveryData.lastChkPointDesc,   // 배송상태
                deliveryTel: deliveryData.lastRouteTel,          // 배송기사 연락처
                recentTrace: deliveryData.lastTrackingDesc,      // 최근 배송 현황
                trackingDetails: deliveryData.tracking.map(track => ({
                  time: `${track.actDate.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')} ${track.actTime.replace(/(\d{2})(\d{2})/, '$1:$2')}`,
                  location: track.stationName || '-',
                  description: track.trackingDesc || track.chkPointDesc || '-',
                  tel: track.stationTel || '-'
                })).reverse()  // 최신 정보가 위로 오도록 역순 정렬
              }
            };
            success(formattedData);
          } else {
            fail(new Error('운송장 정보를 찾을 수 없습니다.'));
          }
        } else {
          fail(new Error('운송장 조회에 실패했습니다.'));
        }
      } catch (error) {
        console.error('Delivery search failed:', error);
        fail(error);
      }
    },

    onPageChange(childPassParam) {
      const { pageType, page } = childPassParam;

      if (pageType === serverEnum.orderStatus.Delivering) {
        this.invoice.delivering.selectedPageNum = page;
      } else if (pageType === serverEnum.orderStatus.Completed) {
        this.invoice.completed.selectedPageNum = page;
      }

      this.paging.currentPage = page;
      this.handleSearch({ pageType });
    },

    // 데이터 처리 관련 메서드들
    async handleSearch(params = {}) {
      this.loadingStates.delivering = true;
      this.loadingStates.completed = true;

      try {
        if (!params?.pageType) {
          this.resetPagination();
        }

        // 쿼리 파라미터 업데이트
        this.updateQueryParams();

        // 기존 검색 로직
        const queryParams = this.getQueryParams();

        if (this.searchFilter.keyword) {
          if (this.searchFilter.filter === 'hospitalName') {
            queryParams.hospitalName = this.searchFilter.keyword;
          } else if (this.searchFilter.filter === 'hospitalId') {
            queryParams.hospitalId = this.searchFilter.keyword;
          }
        }

        await this.processSearchParams(queryParams);
        await this.fetchData(queryParams);
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        this.loadingStates.delivering = false;
        this.loadingStates.completed = false;
      }
    },

    async processSearchParams(params) {
      if (!params?.pageType) {
        this.resetPagination();
      }

      this.updateSearchDates(params);

      if (this.hasInvalidDateRange()) {
        await oneBtnDialog('알림', '종료일을 시작일 이후로 설정해주세요.');
        throw new Error('Invalid date range');
      }
    },

    // API 호출 및 데이터 업데이트
    async fetchData(params) {
      const isCashFlow = this.currentTab === this.tabs.CashFlow;

      try {
        if (!params?.pageType || params.pageType === 'DELIVERING') {
          const deliveringData = await this.apiHandler.getOrders(this, {
            ...params,
            invoiceType: "DELIVERING"
          });
          if (deliveringData) {
            this.updateDeliveringData(deliveringData, isCashFlow);
          }
        }

        if (!params?.pageType || params.pageType === 'COMPLETED') {
          const completedData = await this.apiHandler.getOrders(this, {
            ...params,
            deliveryType: isCashFlow ? "CASHFLOW-COMPLETED" : "DELIVERY-MANAGEMENT-COMPLETED"
          });
          if (completedData) {
            this.updateCompletedData(completedData, isCashFlow);
          }
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        throw error;
      }
    },

    // 유틸리티 메서드들
    resetPagination() {
      this.paging.currentPage = 1;
      this.invoice.delivering.selectedPageNum = 1;
      this.invoice.completed.selectedPageNum = 1;
    },

    updateSearchDates(params) {
      if (params?.startAt !== undefined) {
        this.searchFilter.startAt = params.startAt;
      }
      if (params?.endAt !== undefined) {
        this.searchFilter.endAt = params.endAt;
      }

      if (this.dateRanges?.length === 2) {
        this.searchFilter.startAt = dayjs(this.dateRanges[0]).valueOf();
        this.searchFilter.endAt = dayjs(this.dateRanges[1]).valueOf();
      }
    },

    hasInvalidDateRange() {
      return this.searchFilter.startAt &&
          this.searchFilter.endAt &&
          !compareUnixDates(this.searchFilter.startAt, this.searchFilter.endAt);
    },

    // 데이터 매핑 메서드들
    updateDeliveringData(data, isCashFlow) {
      if (!data) return;

      const {
        docs = [],
        totalDocs = 0,
        totalPages = 0,
        totalDeliveringCount = 0
      } = data;

      const mappedDocs = this.mapOrderDocs(docs, isCashFlow);

      if (isCashFlow) {
        this.updateCashFlowDelivering(mappedDocs, totalDocs, totalDeliveringCount);
      } else {
        this.updateInvoiceDelivering(mappedDocs, totalDocs, totalPages);
      }
    },

    updateCompletedData(data, isCashFlow) {
      if (!data) return;

      const {
        docs = [],
        totalDocs = 0,
        totalPages = 0,
        totalCompletedCount = 0,
        totalReturnedCount = 0,
        totalRemainAmt = '',
        deptName = '',
        empName = '',
        empMobile = ''
      } = data;

      const mappedDocs = this.mapOrderDocs(docs, isCashFlow);

      if (isCashFlow) {
        this.updateCashFlowCompleted(
            mappedDocs,
            totalDocs,
            totalPages,
            totalCompletedCount,
            totalReturnedCount,
            { totalRemainAmt, deptName, empName, empMobile }
        );
      } else {
        this.updateInvoiceCompleted(mappedDocs, totalDocs, totalPages);
      }
    },

    mapOrderDocs(docs, isCashFlow) {
      if (!Array.isArray(docs)) return [];

      return docs.map((item, index) => ({
        ...item,
        index: (item.sortNo || 0) + 1,
        deliveryType: item.deliveryType || '-',
        deliveryNo: item.deliveryNo || '-',
        ...(isCashFlow ? {} : this.getDeliveryManagementFields(item))
      }));
    },

    // 상태 업데이트 메서드들
    updateCashFlowDelivering(mappedDocs, totalDocs, totalDeliveringCount) {
      if (!Array.isArray(mappedDocs)) return;

      this.cashFlow.delivering = {
        list: mappedDocs,
        totalCount: totalDocs || 0
      };
      this.cardInfo.totalDeliveryCount = totalDeliveringCount || 0;
    },

    updateInvoiceDelivering(mappedDocs, totalDocs, totalPages) {
      if (!Array.isArray(mappedDocs)) return;

      this.invoice.delivering = {
        list: mappedDocs,
        totalCount: totalDocs || 0,
        totalPages: totalPages || 1,
        selectedPageNum: this.invoice.delivering.selectedPageNum
      };
    },

    updateCashFlowCompleted(
        mappedDocs,
        totalDocs,
        totalPages,
        totalCompletedCount,
        totalReturnedCount,
        cardInfo
    ) {
      if (!Array.isArray(mappedDocs)) return;

      this.cashFlow.completed = {
        list: mappedDocs,
        totalCount: totalDocs || 0
      };
      this.paging.totalPages = totalPages || 1;
      this.cardInfo.totalCompletedCount = (totalCompletedCount || 0) + (totalReturnedCount || 0);

      this.updateCardInfo(cardInfo);
      this.checkUnpaidAmount(cardInfo.totalRemainAmt);
    },

    updateInvoiceCompleted(mappedDocs, totalDocs, totalPages) {
      if (!Array.isArray(mappedDocs)) return;

      this.invoice.completed = {
        list: mappedDocs,
        totalCount: totalDocs || 0,
        totalPages: totalPages || 1,
        selectedPageNum: this.invoice.completed.selectedPageNum
      };
    },

    updateCardInfo({ totalRemainAmt, deptName, empName, empMobile }) {
      Object.assign(this.cardInfo, {
        totalRemainAmt,
        deptName: deptName || '',
        empName: empName || '',
        empMobile: empMobile || ''
      });
    },

    checkUnpaidAmount(totalRemainAmt) {
      if (totalRemainAmt &&
          parseFloat(totalRemainAmt.replace(/[,-]/g, '')) < 0 &&
          !this.unpaidAmtNoti) {
        oneBtnDialog(
            '알림',
            `현재 ${totalRemainAmt.replace("-", "")}원의 미수금이 발생 했습니다.`
        ).then(() => {
          this.unpaidAmtNoti = true;
        });
      }
    },

    // 이벤트 핸들러 메서드들
    onPagingLimitChange(event) {
      const newLimit = Number(event.target.value);
      this.paging.limit = newLimit;
      this.handleSearch();
    },

    onDateRangeChange(dates) {
      this.dateRange = dates;
      if (dates?.length === 2 && this.selectedPeriod === 'custom') {
        this.searchFilter.startAt = dates[0].valueOf();
        this.searchFilter.endAt = dates[1].valueOf();
        this.handleSearch();
      }
    },

    // 쿼리 파라미터 생성 메서드 추가
    getQueryParams() {
      const { startAt, endAt } = this.searchFilter;
      const params = {
        page: this.paging.currentPage,
        limit: this.pageSize // pageSize 사용
      };

      if (startAt && endAt) {
        params.startAt = formatDate().unixTimeToYYYYMMDD(startAt);
        params.endAt = formatDate().unixTimeToYYYYMMDD(endAt);
      }

      return params;
    },

    // 배송 리 필드 매핑 메서드 추가
    getDeliveryManagementFields(item) {
      if (!item?.originOrder) return {};

      const { originOrder } = item;
      return {
        invoiceNo: originOrder.InvoiceNo || '',
        umOutKindName: originOrder.UMOutKindName || '',
        smProgressTypeName: originOrder.SMProgressTypeName || '',
        qty: this.formatNumberValue(originOrder.Qty),
        totAmt: item.salesAmt || '',
        totCurAmt: this.formatNumberValue(originOrder.TotCurAmt),
        totCurVat: this.formatNumberValue(originOrder.TotCurVat)
      };
    },

    // 숫자 포맷팅 유틸리티 메서드 추가
    formatNumberValue(value) {
      if (!value) return '';
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    setPeriod(period) {
      this.selectedPeriod = period;
      const today = dayjs();
      
      switch (period) {
        case 'all':
          this.dateRange = [];
          this.searchFilter.startAt = null;
          this.searchFilter.endAt = null;
          break;
        case '3months':
          const threeMonthsAgo = today.subtract(3, 'month');
          this.dateRange = [threeMonthsAgo, today];
          this.searchFilter.startAt = threeMonthsAgo.valueOf();
          this.searchFilter.endAt = today.valueOf();
          break;
        case '1year':
          const oneYearAgo = today.subtract(1, 'year');
          this.dateRange = [oneYearAgo, today];
          this.searchFilter.startAt = oneYearAgo.valueOf();
          this.searchFilter.endAt = today.valueOf();
          break;
        case '3years':
          const threeYearsAgo = today.subtract(3, 'year');
          this.dateRange = [threeYearsAgo, today];
          this.searchFilter.startAt = threeYearsAgo.valueOf();
          this.searchFilter.endAt = today.valueOf();
          break;
        case 'custom':
          // 기존 선택된 날짜 유지
          if (!this.dateRange.length) {
            this.dateRange = [today, today];
            this.searchFilter.startAt = today.valueOf();
            this.searchFilter.endAt = today.valueOf();
          }
          break;
      }

      // 날짜가 변경되면 검색 실행 (custom이 아닐 때만)
      if (period !== 'custom') {
        this.handleSearch();
      }
    },

    // 커스텀 기간 엔터 검색 메서드 추가
    handleCustomDateSearch() {
      if (this.selectedPeriod === 'custom' && this.dateRange?.length === 2) {
        this.handleSearch({
          startAt: this.dateRange[0].valueOf(),
          endAt: this.dateRange[1].valueOf()
        });
      }
    },

    // 페이지 사이즈 변경 핸들러 추가
    onPageSizeChange(value) {
      this.pageSize = value;
      this.paging.limit = value;
      this.paging.currentPage = 1; // 이지 사이즈가 변경되면 첫 페이지로 이동
      this.handleSearch(); // 데이터 재조회
    },

    // 검색 초기화 메서드 수정
    resetSearch() {
      this.searchFilter = {
        filter: 'hospitalName',
        keyword: '',
        startAt: null,
        endAt: null,
        selectedDateFilter: 'all',
        dateFilters: [3, 12, 36, 'etc']
      };
      
      // URL에서 검색 관련 쿼리 파라미터 제거
      const query = { ...this.$route.query };
      delete query.hospitalName;
      
      this.$router.replace({ query }).catch(() => {});
      this.handleSearch();
    },

    goToHospitalManagement() {
      this.$router.push('/hospital/management');
    }
  }
});
</script>

<style lang="scss">
@import './order.scss';

// 카드 컴포넌트 스타일 수정
.card {
  background: #FFFFFF;
  border-radius: 14px;
  box-shadow: 0px 2px 20px 0px rgba(0, 0, 0, 0.05);
  border: none;
  width: 100%;
  overflow: hidden;

  .card-header {
    background: transparent;
    border-bottom: 1px solid #E8EBED;
    padding: 1rem 1.5rem;
  }

  .card-body {
    padding: 1.5rem;
  }
}

// 검색 필터 영역 스타일
.search-filter-row {
  display: flex;
  align-items: center;

  &:last-child {
    margin-bottom: 0;
  }

  .search-filter-label {
    width: 120px;
    font-weight: 500;
    color: #2F3337;
    flex-shrink: 0;
  }

  .search-filter-content {
    flex: 1;
    display: flex;
    gap: 1rem;
    align-items: center;

    .filter-select {
      width: 150px;
      flex-shrink: 0;
    }

    .search-input {
      flex: 1;
      max-width: 400px;
    }

    .button-group {
      display: flex;
      gap: 0.5rem;
      flex-shrink: 0;

      .search-button, .reset-button {
        height: 32px;
        padding: 0 1.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    .date-filter-buttons {
      display: flex;
      gap: 0.5rem;
      flex-wrap: nowrap;

      .period-button {
        height: 32px;
        padding: 0 1rem;
        white-space: nowrap;
      }
    }

    .date-picker {
      width: 280px;
      flex-shrink: 0;
    }
  }
}

// 테이블 영역 스타일
.table-container {
  padding: 0;
  overflow-x: auto;

  .ant-table-wrapper {
    margin: 0;
    
    .ant-table {
      background: transparent;
    }

    .ant-table-container {
      border: none;
    }
  }
}

.table-info {
  font-size: 0.875rem;
  color: #6E747C;
  margin-bottom: 0;

  .text-primary {
    color: #278AEC !important;
    font-weight: 600;
  }
}

.gap-3 {
  gap: 1rem;
}

.page-size-select {
  width: 120px !important;
}

// 탭 스타일 조정
.ant-tabs {
  .ant-tabs-nav {
    margin-bottom: 1rem;
    padding: 0 1.5rem;
  }

  .ant-tabs-content-holder {
    overflow-x: auto;
  }
}

// 검색 필터 영역 공통 높이 설정
$input-height: 36px;

.order-search-filter-row {
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;

  &:last-child {
    margin-bottom: 0;
  }

  .search-filter-label {
    width: 120px;
    font-weight: 500;
    color: #2F3337;
    flex-shrink: 0;
  }

  .search-filter-content {
    flex: 1;
    display: flex;
    gap: 1rem;
    align-items: center;

    .filter-select {
      width: 150px;
      flex-shrink: 0;
      
      :deep(.ant-select-selector) {
        height: $input-height !important;
        line-height: $input-height !important;

        .ant-select-selection-search-input {
          height: $input-height !important;
        }

        .ant-select-selection-item {
          line-height: $input-height !important;
        }
      }
    }

    .search-input {
      flex: 1;
      max-width: 400px;

      :deep(.ant-input-affix-wrapper) {
        height: $input-height !important;
        line-height: $input-height !important;
        padding-top: 0;
        padding-bottom: 0;

        .ant-input {
          height: $input-height - 2px !important;
          line-height: $input-height - 2px !important;
        }
      }

      :deep(.ant-input-search-button) {
        height: $input-height !important;
        line-height: $input-height !important;
      }
    }

    .button-group {
      display: flex;
      gap: 0.5rem;
      flex-shrink: 0;

      .search-button,
      .reset-button {
        height: $input-height !important;
        line-height: $input-height - 2px !important;
        padding: 0 20px;
      }
    }

    .date-filter-buttons {
      display: flex;
      gap: 0.5rem;
      flex-wrap: nowrap;

      .period-button {
        height: $input-height !important;
        line-height: $input-height - 2px !important;
        padding: 0 16px;
      }
    }

    .date-picker {
      :deep(.ant-picker) {
        height: $input-height !important;
        
        .ant-picker-input > input {
          height: $input-height - 2px !important;
          line-height: $input-height - 2px !important;
        }
      }
    }
  }
}

// 페이지 사이즈 선택 스타일
.page-size-select {
  :deep(.ant-select-selector) {
    height: $input-height !important;
    line-height: $input-height !important;

    .ant-select-selection-item {
      line-height: $input-height !important;
    }
  }
}
</style>
